import { createContext, useContext, useEffect, useRef, useState, useCallback, ReactNode } from 'react';
import api from '@/lib/api';
import { useAuth } from './AuthContext';

export interface Reminder {
  id: string;
  title: string;
  dueAt: string;
  status: string;
  leadId: string | null;
  lead: { id: string; name: string } | null;
}

interface ReminderContextValue {
  dueReminders: Reminder[];
  pendingReminders: Reminder[];
  pendingCount: number;
  snooze: (id: string, minutes: number) => Promise<void>;
  markDone: (id: string) => Promise<void>;
  dismiss: (id: string) => Promise<void>;
  refresh: () => void;
}

const ReminderContext = createContext<ReminderContextValue | null>(null);

// Soft ding using Web Audio API
function playDing() {
  try {
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(440, ctx.currentTime + 0.4);
    gain.gain.setValueAtTime(0.3, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.5);
  } catch {}
}

function sendBrowserNotification(title: string, body: string) {
  if (Notification.permission === 'granted') {
    new Notification(title, { body, icon: '/vite.svg' });
  }
}

export function ReminderProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [dueReminders, setDueReminders] = useState<Reminder[]>([]);
  const [pendingReminders, setPendingReminders] = useState<Reminder[]>([]);
  const seenIds = useRef<Set<string>>(new Set());
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Request browser notification permission once on login
  useEffect(() => {
    if (user && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, [user]);

  const fetchDue = useCallback(async () => {
    if (!user) return;
    try {
      const [dueRes, allRes] = await Promise.all([
        api.get('/reminders/due'),
        api.get('/reminders'),
      ]);
      const due: Reminder[] = dueRes.data;
      const all: Reminder[] = allRes.data;
      setPendingReminders(all);

      // Only alert for newly discovered due reminders
      const newOnes = due.filter(r => !seenIds.current.has(r.id));
      if (newOnes.length > 0) {
        playDing();
        newOnes.forEach(r => {
          seenIds.current.add(r.id);
          sendBrowserNotification(
            `Reminder: ${r.title}`,
            r.lead ? `Lead: ${r.lead.name}` : 'BrixLead',
          );
        });
      }
      setDueReminders(due);
    } catch {}
  }, [user]);

  const refresh = useCallback(() => { fetchDue(); }, [fetchDue]);

  useEffect(() => {
    if (!user) return;
    fetchDue();
    intervalRef.current = setInterval(fetchDue, 60_000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [user, fetchDue]);

  const removeFromDue = (id: string) => {
    setDueReminders(prev => prev.filter(r => r.id !== id));
    setPendingReminders(prev => prev.filter(r => r.id !== id));
    // Keep in seenIds so it doesn't re-alert if it briefly reappears during poll
  };

  const updateInLists = (id: string, updated: Reminder) => {
    setDueReminders(prev => prev.map(r => r.id === id ? updated : r));
    setPendingReminders(prev => prev.map(r => r.id === id ? updated : r));
  };

  const snooze = async (id: string, minutes: number) => {
    try {
      const { data } = await api.patch(`/reminders/${id}/snooze`, { minutes });
      seenIds.current.delete(id); // allow re-alert when it becomes due again
      setDueReminders(prev => prev.filter(r => r.id !== id));
      setPendingReminders(prev => prev.map(r => r.id === id ? data : r));
    } catch {}
  };

  const markDone = async (id: string) => {
    // Immediately remove from UI
    removeFromDue(id);
    try {
      await api.patch(`/reminders/${id}/done`);
      // Re-fetch to ensure sync with server
      fetchDue();
    } catch {
      // If API fails, re-fetch to restore correct state
      fetchDue();
    }
  };

  const dismiss = async (id: string) => {
    try {
      await api.patch(`/reminders/${id}/dismiss`);
      removeFromDue(id);
    } catch {}
  };

  return (
    <ReminderContext.Provider value={{
      dueReminders,
      pendingReminders,
      pendingCount: pendingReminders.length,
      snooze,
      markDone,
      dismiss,
      refresh,
    }}>
      {children}
    </ReminderContext.Provider>
  );
}

export function useReminders() {
  const ctx = useContext(ReminderContext);
  if (!ctx) throw new Error('useReminders must be used within ReminderProvider');
  return ctx;
}
